<template>
  <div class="notifications">
    <div class="flex flex-col py-2">
      <h5 class="font-semibold">Additional Email Recipients</h5>
      <p class="text-sm py-3">
        Your primary user email address will receive all notifications automatically, however if
        there are others in your office or company who would like to receive the emails as well,
        please enter their email address below.
      </p>

      <VueTagsInput
        v-model="email"
        :tags="emails"
        @tags-changed="updateEmail"
        :placeholder="'Add an email'"
        :validation="tagsValidation"
      />
    </div>

    <h5 class="py-1 font-semibold">Send me a notification email when:</h5>

    <div class="flex items-center py-1">
      <input
        id="notifyAllReportsReadyOwn"
        type="checkbox"
        class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
        v-model="notifications.notifyAllReportsReadyOwn"
      />
      <label for="notifyAllReportsReadyOwn" class="ml-5"
        ><span class="font-semibold">ALL</span> of the reports for an application that was submitted
        to me are ready</label
      >
    </div>
    <Line />

    <template v-if="getCurrentUser.can_admin_company">
      <div class="flex items-center py-1">
        <input
          id="notifyAllReportsReadyShared"
          type="checkbox"
          class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
          v-model="notifications.notifyAllReportsReadyShared"
        />
        <label for="notifyAllReportsReadyShared" class="ml-5"
          ><span class="font-semibold">ALL</span> the reports for an application that I have
          permission to view are ready</label
        >
      </div>
      <Line />
    </template>

    <div class="flex items-center py-1">
      <input
        id="notifySomeReportsReadyOwn"
        type="checkbox"
        class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
        v-model="notifications.notifySomeReportsReadyOwn"
      />
      <label for="notifySomeReportsReadyOwn" class="ml-5"
        ><span class="font-semibold">EACH</span> report for an application that was submitted to me
        is ready</label
      >
    </div>
    <Line />

    <template v-if="getCurrentUser.can_admin_company">
      <div class="flex items-center py-1">
        <input
          id="notifySomeReportsReadyShared"
          type="checkbox"
          class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
          v-model="notifications.notifySomeReportsReadyShared"
        />
        <label for="notifySomeReportsReadyShared" class="ml-5"
          ><span class="font-semibold">EACH</span> report for an application that I have permission
          to view is ready</label
        >
      </div>
      <Line />
    </template>

    <div class="flex items-center py-1">
      <input
        id="notifyApplicationSubmittedOwn"
        type="checkbox"
        class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
        v-model="notifications.notifyApplicationSubmittedOwn"
      />
      <label for="notifyApplicationSubmittedOwn" class="ml-5"
        >An application is submitted to me (whether or not screening reports are ready)</label
      >
    </div>
    <Line />

    <template v-if="getCurrentUser.can_admin_company">
      <div class="flex items-center py-1">
        <input
          id="notifyApplicationSubmittedShared"
          type="checkbox"
          class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
          v-model="notifications.notifyApplicationSubmittedShared"
        />
        <label for="notifyApplicationSubmittedShared" class="ml-5"
          >An application is submitted that I have permission to view (whether or not screening
          reports are ready)</label
        >
      </div>
      <Line />
    </template>

    <template
      v-if="getCurrentUser.landlord.payments_enabled && getCurrentUser.landlord.show_my_payments"
    >
      <div class="flex items-center py-1">
        <input
          id="notifyWithdrawalRequestReceivedOwn"
          type="checkbox"
          class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
          v-model="notifications.notifyWithdrawalRequestReceivedOwn"
        />
        <label for="notifyWithdrawalRequestReceivedOwn" class="ml-5"
          >A My Payments withdrawal request is received</label
        >
      </div>
      <Line />
    </template>

    <template v-if="getCurrentUser.landlord.payments_enabled && getCurrentUser.can_admin_company">
      <div class="flex items-center py-1">
        <input
          id="notifyWithdrawalRequestReceivedShared"
          type="checkbox"
          class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
          v-model="notifications.notifyWithdrawalRequestReceivedShared"
        />
        <label for="notifyWithdrawalRequestReceivedShared" class="ml-5"
          >A My Payments withdrawal request that I have permission to view is received</label
        >
      </div>
      <Line />
    </template>

    <template
      v-if="getCurrentUser.landlord.payments_enabled && getCurrentUser.landlord.show_my_payments"
    >
      <div class="flex items-center py-1">
        <input
          id="notifyWithdrawalRequestApprovedOwn"
          type="checkbox"
          class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
          v-model="notifications.notifyWithdrawalRequestApprovedOwn"
        />
        <label for="notifyWithdrawalRequestApprovedOwn" class="ml-5"
          >A My Payments withdrawal request is approved</label
        >
      </div>
      <Line />
    </template>

    <template v-if="getCurrentUser.landlord.payments_enabled && getCurrentUser.can_admin_company">
      <div class="flex items-center py-1">
        <input
          id="notifyWithdrawalRequestApprovedShared"
          type="checkbox"
          class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
          v-model="notifications.notifyWithdrawalRequestApprovedShared"
        />
        <label for="notifyWithdrawalRequestApprovedShared" class="ml-5"
          >A My Payments withdrawal request that I have permission to view is approved</label
        >
      </div>
      <Line />
    </template>

    <template
      v-if="getCurrentUser.landlord.payments_enabled && getCurrentUser.landlord.show_my_payments"
    >
      <div class="flex items-center py-1">
        <input
          id="notifyWithdrawalRequestSucceededOwn"
          type="checkbox"
          class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
          v-model="notifications.notifyWithdrawalRequestSucceededOwn"
        />
        <label for="notifyWithdrawalRequestSucceededOwn" class="ml-5"
          >A My Payments withdrawal request has successfully finished processing</label
        >
      </div>
      <Line />
    </template>

    <template v-if="getCurrentUser.landlord.payments_enabled && getCurrentUser.can_admin_company">
      <div class="flex items-center py-1">
        <input
          id="notifyWithdrawalRequestSucceededShared"
          type="checkbox"
          class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
          v-model="notifications.notifyWithdrawalRequestSucceededShared"
        />
        <label for="notifyWithdrawalRequestSucceededShared" class="ml-5"
          >A My Payments withdrawal request that I have permission to view has successfully finished
          processing</label
        >
      </div>
      <Line />
    </template>

    <template
      v-if="getCurrentUser.landlord.payments_enabled && getCurrentUser.landlord.show_my_payments"
    >
      <div class="flex items-center py-1">
        <input
          id="notifyWithdrawalRequestFailedOwn"
          type="checkbox"
          class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
          v-model="notifications.notifyWithdrawalRequestFailedOwn"
        />
        <label for="notifyWithdrawalRequestFailedOwn" class="ml-5"
          >A My Payments withdrawal request has failed processing</label
        >
      </div>
      <Line />
    </template>

    <template v-if="getCurrentUser.landlord.payments_enabled && getCurrentUser.can_admin_company">
      <div class="flex items-center py-1">
        <input
          id="notifyWithdrawalRequestFailedShared"
          type="checkbox"
          class="h-5 w-5 rounded-sm border-gray-300 text-brand-green focus:ring-brand-green"
          v-model="notifications.notifyWithdrawalRequestFailedShared"
        />
        <label for="notifyWithdrawalRequestFailedShared" class="ml-5"
          >A My Payments withdrawal request that I have permission to view has failed
          processing</label
        >
      </div>
      <Line />
    </template>

    <!-- <h5 class="py-1 font-semibold">Text Message Notifications:</h5>

    <div class="flex items-center py-1">
      <input
        id="smsEnabled"
        type="checkbox"
        class="
          h-5
          w-5
          rounded-sm
          border-gray-300
          text-brand-green
          focus:ring-brand-green
        "
        v-model="notifications.smsEnabled"
      />
      <label for="smsEnabled" class="ml-5"
        >Send me a text message whenever I receive an email notification for
        application submitted</label
      >
    </div>

    <div class="flex flex-col py-2 md:w-1/3" v-if="notifications.smsEnabled">
      <SingleLineInput
        id="contactPhone"
        type="text"
        :value="user.contactPhone"
        @input="user.contactPhone = $event.target.value"
        placeholder="Mobile Phone Number"
        mask="phone"
      ></SingleLineInput>
    </div> -->

    <div class="flex items-center justify-center pt-10">
      <div class="w-full md:max-w-1/3">
        <PrimaryButton @click="update" :disabled="isUserLoading" :is-loading="isUserLoading"
          >Update preferences</PrimaryButton
        >
      </div>
    </div>
  </div>
  <ActionToast ref="notificationsToast" />
</template>
<script>
import { mapGetters } from "vuex"

import { USER_UPDATE } from "@/store/types"

import { camelCase } from "change-case"
import validator from "validator"

import VueTagsInput from "@sipec/vue3-tags-input"

export default {
  components: {
    VueTagsInput
  },
  computed: {
    ...mapGetters(["getCurrentUser", "isUserLoading"])
  },
  mounted() {
    // this.user = {
    //   contactPhone: this.getCurrentUser.landlord.contact_phone
    // }

    if (this.getCurrentUser.landlord.landlord_emails) {
      this.getCurrentUser.landlord.landlord_emails.split("\n").map((email) => {
        this.emails.push({ text: email })
      })
    }

    const notifications = {}

    Object.keys(this.getCurrentUser.notification_settings).forEach((key) => {
      notifications[camelCase(key)] = this.getCurrentUser.notification_settings[key]
    })

    this.notifications = notifications
  },
  data() {
    return {
      user: {},
      notifications: {},
      email: "",
      emails: [],
      tagsValidation: [
        {
          classes: "email",
          disableAdd: true,
          rule: ({ text }) => {
            const valid = validator.isEmail(text)
            return !valid
          }
        }
      ]
    }
  },
  methods: {
    async update() {
      const that = this
      this.user.landlordEmails = this.emails.map((email) => email.text).join("\n")

      try {
        await that.$store.dispatch(USER_UPDATE, {
          landlord: this.user,
          notifications: this.notifications
        })
        this.$refs.notificationsToast.show({
          type: "success",
          hasClose: true,
          content: "Application shared",
          icon: "check"
        })
      } catch (err) {
        this.$refs.notificationsToast.show({
          type: "danger",
          hasClose: true,
          content: "Error has occured."
        })
      }
    },
    updateEmail(newTags) {
      this.emails = newTags
    }
  }
}
</script>
<style lang="scss">
.notifications {
  .vue-tags-input {
    @apply max-w-none;

    .ti-input {
      @apply rounded h-36;

      .ti-new-tag-input-wrapper {
        @apply h-9;
      }

      input.ti-new-tag-input {
        @apply outline-none ring-transparent placeholder-brand-mid-gray;
      }
    }

    .ti-tag {
      @apply bg-brand-green h-9;

      .ti-content {
        .ti-tag-center {
          span {
            @apply text-white;
          }
        }
      }
    }
  }
  .ti-focus {
    .ti-input {
      @apply border-2 border-brand-green;
    }
  }
}
</style>
